import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Edit, Trash2, Calendar, DollarSign, Hash, FileText, MapPin, Wrench, ExternalLink, Building2, Phone, Mail } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import MaintenanceLog from '../components/asset/MaintenanceLog';
import ImageGallery from '../components/asset/ImageGallery';
import DocumentsList from '../components/asset/DocumentsList';
import ExternalLinksEditor from '../components/asset/ExternalLinksEditor';
import Button from '../components/ui/Button';
import { loadContractors } from '../utils/storage';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/Dialog';
import { loadAssets, saveAssets, generateId, loadAssetTypes } from '../utils/storage';
import { getServiceStatus } from '../utils/scheduling';

const AssetDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [assetTypes, setAssetTypes] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const [assets, types, contractorList] = await Promise.all([
        loadAssets(),
        loadAssetTypes(),
        loadContractors()
      ]);
      const found = assets.find(a => a.id === id);
      if (found) {
        setAsset(found);
      } else {
        navigate('/dashboard');
      }
      setAssetTypes(types);
      setContractors(contractorList);
      setLoading(false);
    };
    fetchData();
  }, [id, navigate]);

  const getTypeLabel = (type) => {
    const types = {
      appliance: 'Appliance',
      vehicle: 'Vehicle',
      hvac: 'HVAC',
      plumbing: 'Plumbing',
      other: 'Other'
    };
    return types[type] || type;
  };

  const handleImageUpdate = async (images) => {
    const assets = await loadAssets();
    const updatedAssets = assets.map(a =>
      a.id === id ? { ...a, images } : a
    );
    await saveAssets(updatedAssets);
    const updated = updatedAssets.find(a => a.id === id);
    setAsset(updated);
  };

  const handleDocumentUpdate = async (documents) => {
    const assets = await loadAssets();
    const updatedAssets = assets.map(a =>
      a.id === id ? { ...a, documents } : a
    );
    await saveAssets(updatedAssets);
    const updated = updatedAssets.find(a => a.id === id);
    setAsset(updated);
  };

  const handleAddMaintenance = async (maintenanceData) => {
    const assets = await loadAssets();
    const newRecord = {
      id: generateId(),
      ...maintenanceData
    };
    const updatedAssets = assets.map(a =>
      a.id === id
        ? { ...a, maintenanceRecords: [...(a.maintenanceRecords || []), newRecord] }
        : a
    );
    await saveAssets(updatedAssets);
    const updated = updatedAssets.find(a => a.id === id);
    setAsset(updated);
  };

  const handleDeleteMaintenance = async (recordId) => {
    if (!window.confirm('Delete this maintenance record?')) return;
    const assets = await loadAssets();
    const updatedAssets = assets.map(a =>
      a.id === id
        ? { ...a, maintenanceRecords: (a.maintenanceRecords || []).filter(r => r.id !== recordId) }
        : a
    );
    await saveAssets(updatedAssets);
    const updated = updatedAssets.find(a => a.id === id);
    setAsset(updated);
  };

  const handleDelete = async () => {
    const assets = await loadAssets();
    const updated = assets.filter(a => a.id !== id);
    await saveAssets(updated);
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-12 px-4 text-center">Loading...</div>
      </>
    );
  }

  if (!asset) {
    return null;
  }

  const serviceStatus = getServiceStatus(asset.nextServiceDate);

  // Get the asset type definition
  const assetType = assetTypes.find(t => t.value === asset.type);
  const attributes = assetType?.attributes || [];

  // Fields to exclude from dynamic display (shown separately or already shown)
  const excludedFields = new Set([
    'name',           // Shown in header
    'type',           // Shown in header
    'notes',          // Shown in dedicated section
    'externalLinks',  // Shown in dedicated section
    'images',         // Shown in ImageGallery
    'documents',      // Shown in DocumentsList
    'maintenanceRecords', // Shown in MaintenanceLog
    'contractors',    // Shown in dedicated section
    'id'              // Internal field
  ]);

  // Get icon for field type
  const getIconForField = (fieldName, fieldType) => {
    if (fieldName.includes('date') || fieldName.includes('Date')) {
      return Calendar;
    }
    if (fieldName.includes('cost') || fieldName.includes('value') || fieldName.includes('Cost') || fieldName.includes('Value')) {
      return DollarSign;
    }
    if (fieldName.includes('location') || fieldName.includes('Location')) {
      return MapPin;
    }
    if (fieldName.includes('service') || fieldName.includes('Service')) {
      return Wrench;
    }
    if (fieldName.includes('serial') || fieldName.includes('Serial') || fieldName.includes('vin') || fieldName.includes('VIN')) {
      return Hash;
    }
    return FileText;
  };

  // Format field value based on type
  const formatFieldValue = (value, attr) => {
    // For numbers, 0 is a valid value, so check differently
    if (attr.type === 'number') {
      if (value === null || value === undefined || value === '') return null;
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return null;
      if (attr.name === 'cost' || attr.name === 'replacementValue') {
        return `$${numValue.toFixed(2)}`;
      }
      return numValue.toLocaleString();
    }
    
    // For other types, empty string means no value
    if (!value || value === '') return null;
    
    switch (attr.type) {
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'textarea':
        return value;
      default:
        return value;
    }
  };

  // Filter attributes to show (exclude name, type, notes, externalLinks, etc.)
  const displayAttributes = attributes.filter(attr => !excludedFields.has(attr.name));

  // Group attributes for better layout
  const dateAttributes = displayAttributes.filter(attr => attr.type === 'date');
  const numberAttributes = displayAttributes.filter(attr => attr.type === 'number');
  const textAttributes = displayAttributes.filter(attr => attr.type === 'text');
  const otherAttributes = displayAttributes.filter(attr => 
    attr.type !== 'date' && attr.type !== 'number' && attr.type !== 'text' && attr.type !== 'textarea'
  );

  return (
    <>
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-6"
        >
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Dashboard</span>
          </Button>

          <div className="glassmorphic p-6 sm:p-8">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h1 className="text-3xl font-light text-foreground mb-2">{asset.name}</h1>
                <p className="text-muted-foreground">{getTypeLabel(asset.type)}</p>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/asset/edit/${id}`)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Date fields */}
              {dateAttributes.map(attr => {
                const value = formatFieldValue(asset[attr.name], attr);
                if (!value) return null;
                const Icon = getIconForField(attr.name, attr.type);
                const isNextService = attr.name === 'nextServiceDate';
                
                return (
                  <div key={attr.name} className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <div className={`flex items-center ${isNextService ? 'justify-between' : 'space-x-2'} mb-1`}>
                      <div className="flex items-center space-x-2">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground uppercase tracking-wide">{attr.label}</span>
                      </div>
                      {isNextService && serviceStatus && (
                        <span className={`px-2 py-1 text-xs rounded ${
                          serviceStatus.variant === 'destructive' ? 'bg-destructive/20 text-destructive' : 
                          serviceStatus.variant === 'warning' ? 'bg-primary/20 text-primary' : 
                          'bg-primary/10 text-primary'
                        }`}>
                          {serviceStatus.text}
                        </span>
                      )}
                    </div>
                    <p className="text-lg font-medium text-foreground">{value}</p>
                  </div>
                );
              })}

              {/* Location field (if it exists) */}
              {asset.location && textAttributes.find(a => a.name === 'location') && (
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-center space-x-2 mb-1">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground uppercase tracking-wide">Location</span>
                  </div>
                  <p className="text-lg font-medium text-foreground">{asset.location}</p>
                </div>
              )}

              {/* Number fields */}
              {numberAttributes.map(attr => {
                const value = formatFieldValue(asset[attr.name], attr);
                if (!value) return null;
                const Icon = getIconForField(attr.name, attr.type);
                
                return (
                  <div key={attr.name} className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <div className="flex items-center space-x-2 mb-1">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">{attr.label}</span>
                    </div>
                    <p className="text-lg font-medium text-foreground">{value}</p>
                  </div>
                );
              })}

              {/* Text fields (excluding location which is shown above) */}
              {textAttributes.filter(attr => attr.name !== 'location').map(attr => {
                const value = asset[attr.name];
                // For text fields, only show if there's a value (empty strings are falsy)
                if (value === null || value === undefined || value === '') return null;
                const Icon = getIconForField(attr.name, attr.type);
                
                return (
                  <div key={attr.name} className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <div className="flex items-center space-x-2 mb-1">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">{attr.label}</span>
                    </div>
                    <p className="text-lg font-medium text-foreground">{value}</p>
                  </div>
                );
              })}

              {/* Other field types */}
              {otherAttributes.map(attr => {
                const value = asset[attr.name];
                if (!value || value === '') return null;
                const Icon = getIconForField(attr.name, attr.type);
                
                return (
                  <div key={attr.name} className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <div className="flex items-center space-x-2 mb-1">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">{attr.label}</span>
                    </div>
                    <p className="text-lg font-medium text-foreground">{value}</p>
                  </div>
                );
              })}
            </div>

            {asset.notes && (
              <div className="bg-white/5 rounded-lg p-4 border border-white/10 mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Notes</span>
                </div>
                <p className="text-sm text-body whitespace-pre-wrap">{asset.notes}</p>
              </div>
            )}

            {asset.externalLinks && asset.externalLinks.length > 0 && (
              <div className="bg-white/5 rounded-lg p-4 border border-white/10 mb-6">
                <ExternalLinksEditor links={asset.externalLinks} readOnly={true} />
              </div>
            )}

            {/* Attached Contractors */}
            {asset.contractors && asset.contractors.length > 0 && (
              <div className="bg-white/5 rounded-lg p-4 border border-white/10 mb-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Attached Contractors</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {asset.contractors.map((contractorId) => {
                    const contractor = contractors.find(c => c.id === contractorId);
                    if (!contractor) return null;
                    return (
                      <div key={contractorId} className="bg-white/5 rounded-lg p-4 border border-white/10">
                        <h4 className="font-medium text-foreground mb-2">
                          {contractor.companyName || 'Unnamed Company'}
                        </h4>
                        {contractor.contactName && (
                          <p className="text-sm text-muted-foreground mb-2">{contractor.contactName}</p>
                        )}
                        <div className="space-y-1">
                          {contractor.phone && (
                            <div className="flex items-center space-x-2 text-sm">
                              <Phone className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                              <a href={`tel:${contractor.phone}`} className="text-body hover:text-primary">
                                {contractor.phone}
                              </a>
                            </div>
                          )}
                          {contractor.email && (
                            <div className="flex items-center space-x-2 text-sm">
                              <Mail className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                              <a href={`mailto:${contractor.email}`} className="text-body hover:text-primary truncate">
                                {contractor.email}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="glassmorphic p-6 sm:p-8 mb-6">
            <ImageGallery
              assetId={asset.id}
              images={asset.images || []}
              onUpdate={handleImageUpdate}
            />
          </div>

          <div className="glassmorphic p-6 sm:p-8 mb-6">
            <DocumentsList
              assetId={asset.id}
              documents={asset.documents || []}
              onUpdate={handleDocumentUpdate}
            />
          </div>

          <div className="glassmorphic p-6 sm:p-8">
            <MaintenanceLog
              records={asset.maintenanceRecords || []}
              onAdd={handleAddMaintenance}
              onDelete={handleDeleteMaintenance}
            />
          </div>
        </motion.div>
      </div>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Asset</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{asset.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AssetDetail;

